import { CSSObject } from "@emotion/react";

import { ICON_DEFAULT_STYLE } from "@/shared";
import { useDashboardStore } from "@/stores/useDashboardStore";
import { useMainBannerStore } from "@/stores/useMainBannerStore";

import { colors } from "@dotnet/telematics-design-system";

import useGeneralHeights from "../../shared/hooks/useGeneralHeights";
import { DoughnutCenterItemsLineHeight, DoughnutTopAlignment } from "./config";
import { PaperContentHeights, PaperHeights, SideWidthType } from "./types";

const gap = "8px";
const sideGap = `20px`;

// only half of the 24px margin is visible,
// the other half is hidden by the scrollbar (scrollbarGutter: "stable both-edges" property in contentContainer)
// and visually looks like margin
const bothSidesMargin = "calc(12px * 2)";

const halfOfAGap = `calc(${gap} / 2)`;

const calculateSideWidthToLg = ({ from1099, fromLg }: SideWidthType) =>
  from1099 && !fromLg
    ? `calc(50% - ${sideGap})`
    : `calc(100% - ${bothSidesMargin})`;

const calculateLeftSideWidth = ({ from1099, fromLg }: SideWidthType) =>
  fromLg
    ? `calc(69% - ${sideGap})`
    : calculateSideWidthToLg({ from1099, fromLg });

const calculateRightSideWidth = ({ from1099, fromLg }: SideWidthType) =>
  fromLg
    ? `calc(31% - ${sideGap})`
    : calculateSideWidthToLg({ from1099, fromLg });

const calculateNewsPaperHeight = (fromLg: boolean, from1099: boolean) => {
  const newsHeight = useDashboardStore(state => state.newsHeight);

  const heightFrom1099 =
    from1099 && `${PaperHeights.newsHeightFrom1099} + ${newsHeight}px`;
  const heightFromLg = fromLg && `${PaperHeights.xxxlarge} + ${newsHeight}px`;

  return `calc(${heightFromLg || heightFrom1099 || PaperHeights.xlarge})`;
};

const emptyCardWidth = (toSm: boolean, toMd: boolean, to1099: boolean) => {
  if (toSm) return "90%";
  if (toMd) return "55%";
  if (to1099) return "46%";
  return "70%";
};

const deviceStatusContentHeightToSm = "208px";
const deviceStatusNameHeightInSingleCardToSm = "52px";
const deviceStatusNameMarginBottomInSingleCardToSm = "4px";
const separatorHeight = "1px";
const deviceStatusContentHeightMarginTopToSm = "16px";

interface UseStylesProps {
  to1099?: boolean;
  from1099?: boolean;
  to1600?: boolean;
  fromLg?: boolean;
  toSm?: boolean;
  toLg?: boolean;
  toMd?: boolean;
  diameter?: string;
  item?: any;
  fixedContentHeight?: boolean;
  isClickable?: boolean;
  enlargeListHeight?: boolean;
  isCreditsCard?: boolean;
}

export const useStyles = ({
  to1099 = false,
  from1099 = false,
  to1600 = false,
  fromLg = false,
  toSm = false,
  toLg = false,
  toMd = false,
  diameter,
  item,
  fixedContentHeight,
  isClickable,
  enlargeListHeight,
  isCreditsCard
}: UseStylesProps = {}) => {
  const mainBannerHeight = useMainBannerStore(state => state.height);

  const { DashboardContainerHeight } = useGeneralHeights(mainBannerHeight);

  return {
    dashboardContainer: {
      width: "100%",
      height: "100vh",
      backgroundColor: colors.gray50
    },
    contentContainer: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "center",
      width: "100%",
      gap: gap,
      overflowY: "auto",
      scrollbarGutter: "stable both-edges",
      height: DashboardContainerHeight,
      paddingTop: "2px", // to avoid scrollbar overlap with the top section
      paddingBottom: "12px"
    },

    leftSide: {
      display: "flex",
      flexDirection: "column",
      gap: gap,
      width: calculateLeftSideWidth({ from1099, fromLg })
    },
    rightSide: {
      display: "flex",
      flexDirection: "column",
      gap: gap,
      width: calculateRightSideWidth({ from1099, fromLg })
    },
    dashboardPaper: {
      backgroundColor: colors.white,
      borderRadius: "8px",
      boxShadow:
        "0px 0px 2px 0px rgba(37, 42, 49, 0.16), 0px 1px 4px 0px rgba(37, 42, 49, 0.12)"
    },
    innerPaper: {
      width: fromLg ? `calc(50% - ${halfOfAGap})` : `100%`,
      gap: gap
    },
    leftSidePapersHolder: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "center",
      width: "100%",
      gap: gap
    },
    topSection: {
      height: PaperHeights.xs
    },
    dashboardDownloadsSection: {
      height: PaperHeights.resourceDownloads
    },
    newsVertical: {
      height: calculateNewsPaperHeight(fromLg, from1099)
    },
    creditsPaper: {
      height: "unset"
    },
    companyCardsPaper: {
      height: to1099 ? PaperHeights.sm : PaperHeights.xlarge
    },
    dataCard: {
      height: to1099 ? PaperHeights.medium : PaperHeights.xlarge
    },
    cardTitle: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      margin: "24px 24px 16px 24px"
    },
    cardTitleExtention: {
      display: "flex",
      alignItems: "center"
    },
    cardContentBox: {
      margin: "0 24px 24px 24px",
      // dynamically calculate height of a card content
      height: isCreditsCard
        ? "unset"
        : `calc(100% - ${PaperContentHeights.marginBottom} - ${PaperContentHeights.header})`
    },
    emptyCardBox: {
      width: emptyCardWidth(toSm, toMd, to1099),
      height: "204px",
      textAlign: "center",
      margin: "0 auto",
      marginTop: "32px",
      marginBottom: "16px"
    },
    canvasHolder: {
      width: `${diameter}px`,
      height: `${diameter}px`,
      position: "relative"
    },
    doughnutChartCenterInfo: {
      position: "absolute",
      width: "50%",
      top: `${DoughnutTopAlignment}px`,
      left: "35px",
      textAlign: "center",
      lineHeight: `${DoughnutCenterItemsLineHeight}px`
    },
    singleChartItemBox: {
      display: "flex",
      marginBottom: "8px",
      marginRight: "16px",
      ":hover": {
        cursor: isClickable ? "pointer" : undefined
      }
    },
    chartListBox: {
      width: toSm ? "100%" : "fit-content",
      height:
        toSm || !fixedContentHeight || (fixedContentHeight && enlargeListHeight)
          ? "unset"
          : "166px",
      display: "flex",
      flexDirection: "column",
      flexWrap: toSm ? "unset" : "wrap",
      marginLeft: "24px"
    },
    chartContainer: { display: "flex" },
    chartItemStick: {
      width: "8px",
      height: "32px",
      marginRight: "8px",
      borderRadius: "2px",
      alignSelf: "center"
    },
    chartListItemInfoBlock: {
      display: "flex",
      flexDirection: "column",
      whiteSpace: "nowrap"
    },
    individualChartItemBox: {
      height: toSm ? undefined : "32px",
      alignItems: "center"
    },
    scrollableCardContainer: {
      height: "100%",
      overflowY: "auto",
      scrollbarGutter: "stable",
      marginRight: "-16px"
    },
    scrollableStatusInfoContainer: {
      display: "flex",
      flexDirection: "column",
      overflow: "auto",
      height: toSm
        ? `calc(${deviceStatusContentHeightToSm} - ${deviceStatusNameHeightInSingleCardToSm} - ${deviceStatusNameMarginBottomInSingleCardToSm} - ${separatorHeight} - ${deviceStatusContentHeightMarginTopToSm})`
        : "100%",
      width: "100%",
      scrollbarGutter: "stable",
      marginRight: "-16px"
    },
    bottomShadowBox: {
      position: "absolute",
      bottom: 0,
      width: "100%",
      height: "28px",
      background:
        "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 62.5%)"
    },
    singleStatusInfoBlock: {
      display: "flex",
      flexDirection: toSm ? "column" : "row",
      height: "100%",
      width: toSm ? "calc(100% + 16px)" : "inherit",
      position: "relative"
    },
    statusInfoListItem: {
      display: "flex",
      padding: "2px 16px 6px 16px",
      cursor: "pointer"
    },
    statusInfoListItemWithoutWrapper: {
      display: "flex",
      padding: "2px 8px 2px 16px",
      cursor: "pointer",
      borderRadius: "4px",
      transition: "background-color 0.2s ease-in-out",
      alignItems: "center",
      ":hover": {
        backgroundColor: colors.gray50
      }
    },
    hoverableListItem: {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "center",
      height: "24px"
    },
    listRightArrow: { ...ICON_DEFAULT_STYLE, color: colors.textSecondary },
    divWithCenteredContent: {
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    },
    errorMessageContainer: {
      width: "311px",
      paddingTop: "32px",
      margin: "0 auto",
      textAlign: "center"
    },
    wrappedCardTitleInlineAction: {
      paddingTop: "8px"
    },
    scrollableCardContent: {
      height: "100%",
      overflowY: "auto",
      scrollbarGutter: "stable",
      marginRight: "-16px",
      paddingRight: "8px"
    },
    connectivityTabsBox: {
      display: "flex",
      justifyContent: "center"
    },
    connectivityCustomTabs: {
      fontSize: "14px",
      width: "50%",
      minHeight: "36px", // TAB_HEIGHT
      lineHeight: "12px",
      maxWidth: "unset",
      "@media(max-width: 599px)": {
        minWidth: "unset",
        padding: "unset"
      }
    },
    emptyWidget: {
      height: "92px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    },
    horizontalChartContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "16px"
    },
    horizontalChartListBox: {
      display: "grid",
      gridTemplateColumns: `repeat(${toSm ? 4 : 2}, 1fr)`,
      gridTemplateRows: `repeat(${toSm ? 4 : 2}, 1fr)`,
      gridAutoFlow: "column",
      justifyContent: "flex-start"
    }
  } as CSSObject;
};
